import React, { useContext } from 'react';
import { ThemeProvider } from '../Contexts/ThemeContext';
import { Route, Switch, useRouteMatch, Redirect } from 'react-router-dom';
import { DataDisplayProvider, PendoInitializer, SidebarNavProvider } from '@aidkitorg/component-library';
import { PaymentsPage } from './Payments/PaymentsPage';
import { PaymentBatchDetailsPage } from './Payments/PaymentBatchDetailsPage';
import { PaymentsProvider } from '../Contexts/PaymentsContext';
import { AdminPanelPage } from './AdminPanelPage';
import { AccountsPage } from './Accounts/AccountsPage';
import { get_deployment } from '../API';
import { PublicConfigurationContext, UserInfoContext } from '../Context';
import { AccountsProvider } from '../Contexts/AccountsContext';
import { DashboardsPage } from './Dashboards/DashboardsPage';
import { DashboardBuilderPage } from './Dashboards/DashboardBuilderPage';
import { useLocalizedStrings } from '../Localization';
import { DashboardsProvider } from '../Contexts/DashboardsContext';
import { DashboardPage } from './Dashboards/DashboardPage';
import { SettingsProvider } from '../Contexts/SettingsContext';
import { SettingsPage } from './Settings/SettingsPage';
import { ManageFieldsPage } from './Settings/ManageFieldsPage';
import { ReportsPage } from './Reports/ReportsPage';
import { ReportsBuilderPage } from './Reports/ReportsBuilderPage';
import { ReportViewPage } from './Reports/ReportViewPage';
import { ReportsProvider } from '../Contexts/ReportsContext';
import { NotificationsPage } from './Notifications/NotificationsPage';
import { NotificationRecipientsPage } from './Notifications/NotificationRecipientsPage';
import { NotificationsProvider } from '../Contexts/NotificationsContext';
import { getNavItems, getParentRedirectRoutes } from './navigationConfig';

// TEMPORARY PLACEHOLDER PAGES
// TODO: use AppShell to build out an OverviewPage -- this is just temporary to test out the ThemeProvider
const OverviewPage = () => {
  const L = useLocalizedStrings()
  
  return (
    <AdminPanelPage 
      title={L.admin_panel.nav.home}
      breadcrumbs={[
        {
          label: L.admin_panel.nav.home
        }
      ]}
    >
      <></>
    </AdminPanelPage>
  )
}

const ApplicantsPage = () => {
  const L = useLocalizedStrings()

  return (
    <AdminPanelPage
      title={L.admin_panel.nav.applicants}
      breadcrumbs={[
        {
          label: L.admin_panel.nav.applicants
        }
      ]}
    >
      <></>
    </AdminPanelPage>
  )
}

const ReviewQueuesPage = () => {
  const L = useLocalizedStrings()

  return (
    <AdminPanelPage
      title={L.admin_panel.nav.review_queues}
      breadcrumbs={[
        {
          label: L.admin_panel.nav.review_queues
        }
      ]}
    >
      <></>
    </AdminPanelPage>
  )
}

const UserManagementPage = () => {
  const L = useLocalizedStrings()

  return (
    <AdminPanelPage
      title={L.admin_panel.nav.user_management}
      breadcrumbs={[
        {
          label: L.admin_panel.nav.settings
        }
      ]}
    >
      <></>
    </AdminPanelPage>
  )
}

const AuditLogsPage = () => {
  const L = useLocalizedStrings()

  return (
    <AdminPanelPage
      title={L.admin_panel.nav.audit_logs}
      breadcrumbs={[
        {
          label: L.admin_panel.nav.audit_logs
        }
      ]}
    >
      <></>
    </AdminPanelPage>
  )
}

const SupportPage = () => {
  const L = useLocalizedStrings()

  return (
    <AdminPanelPage
      title={L.admin_panel.nav.support}
      breadcrumbs={[
        {
          label: L.admin_panel.nav.support
        }
      ]}
    >
      <></>
    </AdminPanelPage>
  )
}

export const AdminPanelApp = () => {
  let { path } = useRouteMatch();
  const publicConfig = useContext(PublicConfigurationContext);
  const userInfo = useContext(UserInfoContext);
  const programName = get_deployment();
  const L = useLocalizedStrings();
  
  // Get navigation items and generate automatic redirects
  const navItems = getNavItems(L);
  const parentRedirects = getParentRedirectRoutes(navItems);

  return (
    <ThemeProvider>
      <DataDisplayProvider value={{ displayInUTC: false }}>
        <SidebarNavProvider>
          {
            publicConfig.experimental?.enablePendo && (
              <PendoInitializer visitorData={{
                id: userInfo.uid || "unknown"
              }} accountData={{
                id: programName || "unknown"
              }} />)
          }
          <Switch>
            <Route exact path={`${path}`}>
              <Redirect to={`${path}/home`} />
            </Route>
            
            {/* Automatic redirects for parent routes to their first child */}
            {parentRedirects.map((redirect, index) => (
              <Route 
                key={`redirect-${index}`}
                exact 
                path={redirect.from}
                render={() => <Redirect to={redirect.to} />}
              />
            ))}
            
            <Route path={`${path}/home`} component={OverviewPage} />
            <Route path={`${path}/applicants`} component={ApplicantsPage} />
            <Route path={`${path}/review-queues`} component={ReviewQueuesPage} />
            <Route path={`${path}/audit-logs`} component={AuditLogsPage} />
            <Route path={`${path}/support`} component={SupportPage} />

            {/* PAYMENTS ROUTES */}
            <Route 
              exact
              path={`${path}/payments`} 
              children={ 
                <PaymentsProvider>
                  <PaymentsPage/>
                </PaymentsProvider>
              } 
            />
            <Route 
              path={`${path}/payments/payment-details/:batch_name`}
              children={
                <PaymentsProvider>
                  <PaymentBatchDetailsPage/>
                </PaymentsProvider>
              }
            />

            {/* ACCOUNTS ROUTES */}
            <Route
              path={`${path}/balances`}
              children={
                <AccountsProvider>
                  <AccountsPage/>
                </AccountsProvider>
              }
            />

            {/* DASHBOARDS ROUTES */}
            <Route 
              exact
              path={`${path}/dashboards`}
              children={
                <DashboardsProvider>
                  <DashboardsPage/>
                </DashboardsProvider>
              }
            />
            <Route
              exact
              path={`${path}/dashboards/new`}
              children={
                <ReportsProvider>
                  <DashboardsProvider>
                    <SettingsProvider>
                      <DashboardBuilderPage/>
                    </SettingsProvider>
                  </DashboardsProvider>
                </ReportsProvider>
              }
            />
            <Route
              path={`${path}/dashboards/:dashboardId/edit`}
              children={
                <ReportsProvider>
                  <DashboardsProvider>
                    <SettingsProvider>
                      <DashboardBuilderPage/>
                    </SettingsProvider>
                  </DashboardsProvider>
                </ReportsProvider>
              }
            />
            <Route
              path={[`${path}/dashboards/:dashboard_id/:dashboard_title`, `${path}/dashboards/:dashboard_id`]}
              children={
                <ReportsProvider>
                  <DashboardsProvider>
                    <SettingsProvider>
                      <DashboardPage/>
                    </SettingsProvider>
                  </DashboardsProvider>
                </ReportsProvider>
              }
            />

            {/* REPORTS ROUTES */}
            <Route
              exact
              path={`${path}/reports`}
              render={() => (
                <ReportsProvider>
                  <ReportsPage />
                </ReportsProvider>
              )}
            />
            <Route
              exact
              path={`${path}/reports/new`}
              render={() => (
                <ReportsProvider>
                  <SettingsProvider>
                    <ReportsBuilderPage />
                  </SettingsProvider>
                </ReportsProvider>
              )}
            />
            <Route
              exact
              path={`${path}/reports/:reportId/edit`}
              render={(routeProps) => (
                <ReportsProvider key={routeProps.match.params.reportId}>
                  <SettingsProvider>
                    <ReportsBuilderPage />
                  </SettingsProvider>
                </ReportsProvider>
              )}
            />
            <Route
              path={`${path}/reports/:reportId`}
              render={(routeProps) => (
                <ReportsProvider key={routeProps.match.params.reportId}>
                  <ReportViewPage />
                </ReportsProvider>
              )}
            />

            {/* SETTINGS ROUTES */}
            <Route 
              exact
              path={`${path}/settings/user-management`}
              render={() => (
                <SettingsProvider>
                  <UserManagementPage />
                </SettingsProvider>
              )}
            />
            <Route 
              exact
              path={`${path}/settings/manage-fields`}
              render={() => (
                <SettingsProvider>
                  <ManageFieldsPage />
                </SettingsProvider>
              )}
            />

            {/* NOTIFICATIONS ROUTES */}
            <Route
              exact
              path={`${path}/notifications`}
              children={
                <NotificationsProvider>
                  <NotificationsPage/>
                </NotificationsProvider>
              }
            />
            <Route
              path={`${path}/notifications/notification-details/:targetFieldPrefix`}
              children={
                <NotificationsProvider>
                  <NotificationRecipientsPage/>
                </NotificationsProvider>
              }
            />
            {/* Catch-all redirect to home */}
            <Route path="*">
              <Redirect to={`${path}/home`} />
            </Route>
          </Switch>
        </SidebarNavProvider>
      </DataDisplayProvider>
    </ThemeProvider>      
  )
}

export default AdminPanelApp;